import React, { useCallback, useState } from 'react';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Modal from '@/common/components/Modal/index';
import { Notification } from '@/common/utils/index';
import modalStyles from '@/common/styles/modal-form-primary.less';
import { useCreateResellerMutation } from '@/entities/admin-resellers';
import ResellerFormFields from '@/modules/admin-reseller-form/ResellerFormFields';
import styles from '@/modules/admin-reseller-form/styles.less';

const FORM_ID = 'create-reseller-modal-form';

const CreateResellerModal = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({});

  const mutation = useCreateResellerMutation({
    onSuccess: (response) => {
      Notification.success(t('Reseller created successfully'));
      onCreated?.(response?.data);
      setFormState({});
      onClose();
    },
    onError: () => {
      Notification.error(t('Failed to create reseller'));
    },
  });

  const closeModal = useCallback(() => {
    if (mutation.isPending) return;
    setFormState({});
    onClose();
  }, [mutation.isPending, onClose]);

  const handleSubmit = useCallback(
    async (form) => {
      await mutation.mutateAsync({
        ...form,
        phone: form.phone.trim(),
      });
    },
    [mutation]
  );

  return (
    <Modal
      className={cx(modalStyles.modal, styles.formModal)}
      open={open}
      onClose={closeModal}
      overlay
      overlayOnClose={!mutation.isPending}
      zIndex={1302}
    >
      <div className={modalStyles.modalHeader}>
        <h3>{t('New Reseller')}</h3>
      </div>
      <div className={modalStyles.modalForm}>
        <div className={cx(modalStyles.modalBody, styles.formModalBody)}>
          <ResellerFormFields
            formId={FORM_ID}
            className={styles.form}
            borderless
            onSubmit={handleSubmit}
            onFormStateChange={setFormState}
          />
        </div>
        <div className={styles.modalFooter}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={closeModal}
            disabled={mutation.isPending}
            size="large"
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            color="success"
            disabled={!formState.isDirty || !formState.isValid || mutation.isPending}
            loading={mutation.isPending}
            size="large"
          >
            {t('Create resellers')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateResellerModal;
